import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import { iUser } from "core/kangarooTypes/iUser";
import { deleteUser, getUsers } from "core/Services/AdminServices";
import {
  ChevronDown,
  Edit,
  Loader,
  Plus,
  Search,
  Trash2,
  User as UserIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getStatusColors } from "utils/statusColors";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import ConfirmModal from "shared/ConfirmModal";
import AddUserDialog from "./AddUserDialog";
import EditUserDialog from "./EditUserDialog";
import { useTranslation } from "react-i18next";

const UserManagement = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<iPaginationResponse<iUser>>();
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<iUser | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<iUser | null>(null);

  const roles = [
    { value: "admin", label: "Admin" },
    { value: "operator", label: "Operator" },
    { value: "driver", label: "Driver" },
    { value: "accountant", label: "Accountant" },
  ];

  const {
    register,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      role: undefined,
    },
  });

  const fetchData = async (
    filters?: {
      search: string | undefined;
      role: string | undefined;
    },
    page: number = 1
  ) => {
    try {
      setIsLoading(true);
      const response = await getUsers({ ...filters }, page);
      setUsers(response);
    } catch (error) {
      toast.error(t("userManagement.failedToLoadUsers"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const subscription = watch((values) => {
      const timer = setTimeout(() => {
        fetchData(
          {
            search: values.name || "",
            role: values.role,
          },
          1
        );
        setCurrentPage(1);
      }, 500);

      return () => clearTimeout(timer);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(
      {
        search: watch("name"),
        role: watch("role"),
      },
      page
    );
  };

  const handleAddUser = () => {
    setIsAddDialogOpen(true);
  };

  const handleEditUser = (user: iUser) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleEditSuccess = async () => {
    await fetchData(
      {
        search: watch("name"),
        role: watch("role"),
      },
      currentPage
    );
  };

  const handleDeleteClick = (user: iUser) => {
    setUserToDelete(user);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setDeletingUserId(userToDelete.id);
      await deleteUser(userToDelete.id);
      await fetchData(
        {
          search: watch("name"),
          role: watch("role"),
        },
        currentPage
      );
      toast.success(t("userManagement.userDeletedSuccess"));
      setIsConfirmDeleteOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(
        (error as any)?.response?.data?.message ||
          t("userManagement.failedToDeleteUser")
      );
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteOpen(false);
    setUserToDelete(null);
  };

  const handleAddSuccess = () => {
    fetchData(
      {
        search: watch("name"),
        role: watch("role"),
      },
      currentPage
    );
  };

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Add User Button */}
      <div className="flex justify-end items-center">
        <Button
          icon={<Plus className="w-4 h-4 mr-2" />}
          onClick={handleAddUser}
          className="w-full sm:w-auto"
        >
          {t("userManagement.addNewUser")}
        </Button>
      </div>

      {/* User Management Container */}
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Search and Filter Row */}
        <div className="flex flex-col md:flex-row justify-between gap-4">
          {/* Search Input */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <FormInput
              name="name"
              placeholder={t("userManagement.searchByName")}
              register={register}
              errors={errors}
              leftIcon={<Search size={20} />}
            />
          </div>

          {/* Role Filter */}
          <div className="w-full md:w-1/4">
            <FormInput
              name="role"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("userManagement.role")}
              register={register}
              errors={errors}
              selectOptions={roles}
            />
          </div>
        </div>

        {/* User Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase rounded-tl-lg">
                  {t("userManagement.user")}
                </th>
                <th className="text-left p-3 w-2/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("userManagement.email")}
                </th>
                <th className="text-left p-3 w-2/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("userManagement.phoneNumber")}
                </th>
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("userManagement.role")}
                </th>
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("userManagement.status")}
                </th>
                <th className="text-right p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase rounded-tr-lg">
                  {t("userManagement.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      <td className="py-4 px-3">
                        <div className="flex items-center">
                          <Skeleton
                            circle
                            width={32}
                            height={32}
                            className="mr-3"
                          />
                          <Skeleton width={120} />
                        </div>
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={180} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={180} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} height={24} borderRadius={12} />
                      </td>
                      <td className="py-4 px-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Skeleton width={20} height={20} />
                          <Skeleton width={20} height={20} />
                        </div>
                      </td>
                    </tr>
                  ))
              ) : users?.data && users.data.length > 0 ? (
                users.data.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-4 px-3">
                      <div className="flex items-center">
                        <div className="bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center me-3">
                          {user.id === 1 ? (
                            <UserIcon className="w-4 h-4" />
                          ) : (
                            <div className="text-xs font-medium">
                              {user.first_name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-sm">
                          {user.first_name} {user.last_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {user.email}
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {user.phone_number}
                    </td>
                    <td className="py-4 px-3">
                      <span className="capitalize text-sm">{user.role}</span>
                    </td>
                    <td className="py-4 px-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs
                        ${
                          getStatusColors(
                            user.active ? "active" : "deactivated"
                          ).bg
                        }
                       ${
                         getStatusColors(user.active ? "active" : "deactivated")
                           .text
                       }
                        `}
                      >
                        {user.active
                          ? t("userManagement.active")
                          : t("userManagement.deactivated")}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          className="text-gray-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-gray-100"
                          onClick={() => handleEditUser(user)}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          className={`text-gray-500 hover:text-red-600 transition-colors p-1 rounded hover:bg-gray-100 ${
                            deletingUserId === user.id
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={() => handleDeleteClick(user)}
                          disabled={deletingUserId === user.id}
                        >
                          {deletingUserId === user.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {t("userManagement.noUsersFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && users?.meta && (
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-500">
              {users.data && users.data.length > 0 ? (
                <>
                  {t("userManagement.showing")} {users.meta.from}-
                  {users.meta.to} {t("userManagement.of")} {users.meta.total}{" "}
                  {t("userManagement.entries")}
                </>
              ) : (
                <>0 {t("userManagement.entries")}</>
              )}
            </div>
            {users.data && users.data.length > 0 && (
              <div className="flex space-x-1">
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {t("userManagement.previous")}
                </button>
                <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
                  {currentPage}
                </button>
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  disabled={currentPage === users.meta.last_page}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t("userManagement.next")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddUserDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
      />

      <EditUserDialog
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleEditSuccess}
        user={selectedUser}
      />

      <ConfirmModal
        isOpen={isConfirmDeleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("userManagement.confirmDelete")}
        message={t("userManagement.deleteConfirmMessage")}
        confirmText={t("userManagement.delete")}
        cancelText={t("userManagement.cancel")}
        isLoading={deletingUserId !== null}
        variant="danger"
      />
    </div>
  );
};

export default UserManagement;
